import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { categoryLabel } from '../data/sampleAlerts';

/** Static preview rows for this report template only — not persisted as alerts. */
const REPORT_PREVIEW_THREATS: { category: string; severity: string }[] = [
  { category: 'email', severity: 'high' },
  { category: 'email', severity: 'medium' },
  { category: 'dark_web', severity: 'low' },
  { category: 'device', severity: 'low' },
  { category: 'email', severity: 'high' },
];

function severityRowStyle(sev: string): { badge: string; label: string } {
  const s = sev.toLowerCase();
  if (s === 'high') {
    return {
      badge: 'bg-danger/20 text-danger border-danger/30',
      label: 'High',
    };
  }
  if (s === 'medium') {
    return {
      badge: 'bg-warning/20 text-warning border-warning/30',
      label: 'Medium',
    };
  }
  return {
    badge: 'bg-accent/15 text-accent border-accent/30',
    label: 'Low',
  };
}

const THREAT_DATES_ISO = [
  '2026-04-06T14:30:00.000Z',
  '2026-04-05T11:15:00.000Z',
  '2026-04-04T09:00:00.000Z',
  '2026-04-03T16:45:00.000Z',
  '2026-04-02T08:20:00.000Z',
];

function buildThreatRows() {
  return REPORT_PREVIEW_THREATS.map((a, i) => {
    const iso = THREAT_DATES_ISO[i] ?? THREAT_DATES_ISO[0];
    const date = new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const sev = severityRowStyle(a.severity);
    return {
      date,
      type: categoryLabel(a.category),
      severityLabel: sev.label,
      severityBadge: sev.badge,
      status: 'Blocked',
    };
  });
}

export default function ReportDetailPage() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const complianceFramework =
    userProfile?.compliance_framework?.trim() || 'your applicable regulatory framework';
  const ownerName = userProfile?.owner_name?.trim() || 'Account owner';
  const threatRows = useMemo(() => buildThreatRows(), []);

  const pdfTooltip = 'Available after API integrations';

  if (!currentUser?.id) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center text-muted">
        <p>Sign in to view this report.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <div className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={() => navigate('/reports')}
          className="inline-flex w-fit items-center text-sm font-semibold text-muted transition-colors hover:text-accent"
        >
          ← Back to Reports
        </button>
        <span className="nav-tip-parent relative inline-flex sm:ml-auto">
          <button
            type="button"
            disabled
            aria-label={`Download PDF. ${pdfTooltip}`}
            className="cursor-not-allowed rounded-lg border border-border bg-surface/30 px-4 py-2.5 text-sm font-semibold text-foreground opacity-50 disabled:pointer-events-none sm:px-5 sm:py-3"
          >
            Download PDF
          </button>
          <span className="nav-icon-tooltip" role="tooltip">
            <span className="nav-icon-tooltip-caret" aria-hidden />
            {pdfTooltip}
          </span>
        </span>
      </div>

      <header className="mb-10 space-y-3 sm:mb-12">
        <p className="text-xs font-semibold uppercase tracking-wide text-accent">April 2026</p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Security Summary</h1>
        <p className="text-sm text-muted sm:text-base">
          Prepared for {ownerName}
          <span className="text-muted/70"> · Generated April 1, 2026</span>
        </p>
      </header>

      <div className="space-y-10 sm:space-y-12">
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Executive Summary</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground sm:text-base">
            Your business maintained a strong security posture this month with a health score of 72/100. Five threats
            were detected and blocked before causing any harm.
          </p>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Threats Detected</h2>
          <div className="mt-4 overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[520px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border bg-navy/50">
                  <th className="px-4 py-3.5 font-semibold text-muted">Date</th>
                  <th className="px-4 py-3.5 font-semibold text-muted">Type</th>
                  <th className="px-4 py-3.5 font-semibold text-muted">Severity</th>
                  <th className="px-4 py-3.5 font-semibold text-muted">Status</th>
                </tr>
              </thead>
              <tbody>
                {threatRows.map((row, idx) => (
                  <tr key={idx} className="border-b border-border/80 last:border-0">
                    <td className="whitespace-nowrap px-4 py-3.5 tabular-nums text-foreground">{row.date}</td>
                    <td className="px-4 py-3.5 text-foreground">{row.type}</td>
                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${row.severityBadge}`}
                      >
                        {row.severityLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="font-medium text-accent">{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Dark Web Monitoring</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground sm:text-base">
            No instances of your business email or domain were found on the dark web this month.
          </p>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Recommendations for Next Month</h2>
          <ul className="mt-4 list-disc space-y-3 pl-5 text-sm leading-relaxed text-foreground sm:text-base">
            <li>Connect your Gmail or Microsoft 365 inbox for deeper email threat monitoring</li>
            <li>Add employee email addresses to expand your monitoring coverage</li>
            <li>Set up WhatsApp alerts for instant threat notifications</li>
          </ul>
        </section>

        <section className="rounded-xl border border-accent/25 bg-accent/5 p-5 sm:p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-accent">Compliance Statement</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground sm:text-base">
            This report supports your obligations under {complianceFramework}. ShieldSME monitoring provides documented
            evidence of security controls in place.
          </p>
        </section>
      </div>
    </div>
  );
}
